import React, { useEffect } from 'react';

import { Box, CircularProgress } from '@mui/material';

import ImageCarousel from './components/ImagesCarousel';
import RegistrationPromptModal from './components/RegistrationPromptModal';
import { useOnboardingFlow } from './hooks/useOnboardingFlow';
import MobileCockpit from './MobileCockpit';
import * as S from './styles';
import { RefreshTokenDebug } from 'components/RefreshTokenDebug';
import { useTab } from 'global/TabContext';
import { useMenuTitle } from 'hooks/useMenuTitleContext';
import Agenda from 'pages/Cockpit/components/Agenda';
import { useEvents } from 'pages/Cockpit/components/Agenda/hooks/useEvents';
import { useNextEvents } from 'pages/Cockpit/components/Agenda/hooks/useNextEvents';
import { useIsMobile, useIsTablet } from 'utils/mediaQueries';

interface CockpitProps {
  isAgenda: boolean;
}

export const Cockpit: React.FC<CockpitProps> = ({ isAgenda = false }) => {
  const { eventsToday } = useEvents();
  const [activeTab] = React.useState('tab_cockpit');
  const { setActiveTabContext } = useTab();
  const { updateMenuTitle } = useMenuTitle();

  // Fetch next events for onboarding flow
  const { nextEvents, loading: loadingNextEvents } = useNextEvents();
  const nextEvent = nextEvents.length > 0 ? nextEvents[0] : null;

  // Onboarding flow hook - pass first next event
  const {
    shouldShowOnboarding,
    shouldShowEventModal,
    completionStatus,
    handleDoItNow,
    handleRemindLater,
    isLoading,
  } = useOnboardingFlow(nextEvent);

  useEffect(() => {
    setActiveTabContext(activeTab);
  }, [activeTab, setActiveTabContext, updateMenuTitle]);

  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  // Show loading spinner while loading next events or evaluating onboarding flow
  if (loadingNextEvents || isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        aria-live="polite"
        aria-busy="true"
      >
        <CircularProgress size={48} aria-label="Carregando..." />
      </Box>
    );
  }

  console.log('[Cockpit] flags', { shouldShowOnboarding, shouldShowEventModal });
  return (
    <>
      {/* Registration Prompt Modal - shows when form is incomplete and event is not starting soon */}
      {shouldShowOnboarding && !shouldShowEventModal && (
        <RegistrationPromptModal
          isOpen={shouldShowOnboarding}
          onDoItNow={handleDoItNow}
          onRemindLater={handleRemindLater}
          completionPercentage={completionStatus?.completionPercentage ?? 0}
        />
      )}

      {/* Main Cockpit Content */}
      {isMobile || isTablet ? (
        <S.MobileSection>
          <MobileCockpit events={eventsToday} />
        </S.MobileSection>
      ) : (
        <S.Wrapper>
          <S.Container>
            <S.Content>
              {!isAgenda && (
                <>
                  <S.Section>
                    <ImageCarousel />
                  </S.Section>
                </>
              )}
              <S.Section>
                <Agenda />
              </S.Section>
            </S.Content>
          </S.Container>
          <RefreshTokenDebug show={false} />
        </S.Wrapper>
      )}
    </>
  );
};
